/* eslint-disable react-hooks/exhaustive-deps */
/**************************************************************************************************************************
 Copyright (c) 2024 WorkEarly Results LLP

     Name: projects.section.tsx
   Author: Harikrishnan Gangadharan
 Comments: 

/**************************************************************************************************************************
 IMPORTS
***************************************************************************************************************************/
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MdClose, MdOutlineError, MdOutlineOpenInNew } from 'react-icons/md';
import { motion } from 'framer-motion';
import { listAll, ref } from 'firebase/storage';

import Section from '@/shared/section';
import FirebaseService from '@/services';
import { Spinner } from '@nextui-org/react';
/**************************************************************************************************************************
 TYPES
***************************************************************************************************************************/
enum ProjectState {
  LOADING = 1,
  SUCCESS = 2,
  FAILED = 3
}
/**************************************************************************************************************************
 IMPLEMENTATIONS
***************************************************************************************************************************/
const Projects: FunctionComponent = () => {
  const { t } = useTranslation('projects');

  const projectRef = ref(FirebaseService.Storage, 'projects');

  const [projects, setProjects] = useState<string[]>([]);

  const [status, setStatus] = useState<ProjectState>(ProjectState.LOADING);

  const [modal, setModal] = useState<boolean>(false);

  const [tempImgSrc, setTempImgSrc] = useState<string>('');

  const onImgClick = (src: string) => {
    setTempImgSrc(src);
    setModal(true);
  };

  useEffect(() => {
    listAll(projectRef)
      .then((res) => {
        let items: string[] = [];
        res.items.forEach((item) =>
          items.push(
            `https://firebasestorage.googleapis.com/v0/b/org-website-c36cf.appspot.com/o/${encodeURIComponent(
              item.fullPath
            )}?alt=media&token=7a7e7558-3d61-425b-a8fa-0371dd66e437`
          )
        );
        setProjects(items);
        setTimeout(() => setStatus(ProjectState.SUCCESS), 2000);
      })
      .catch((error) => {
        console.log(error);
        setTimeout(() => setStatus(ProjectState.FAILED), 2000);
      });
  }, []);

  return (
    <Section id="projects" className="bg-black/80">
      <div className="w-full flex flex-col items-center justify-start gap-5">
        {status === ProjectState.FAILED ? (
          <div className="w-full bg-red-500 relative flex items-center justify-start gap-[8px] rounded-lg p-5">
            <div className="w-[25px] h-[25px] flex items-center justify-center">
              <MdOutlineError className="text-xl text-white" />
            </div>
            <p className="w-[calc(100%-33px)]text-lg text-white font-inter font-light">
              {t('projects.status.error')}
            </p>
          </div>
        ) : status === ProjectState.SUCCESS ? (
          <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {projects.map((src, idx: number) => (
              <motion.div
                className="w-full h-fit flex flex-col items-center justify-start gap-2"
                initial="initial"
                whileInView="entry"
                viewport={{ once: true }}
                variants={{
                  initial: {
                    x: 50 + idx * 10,
                    opacity: 0
                  },
                  entry: {
                    x: 0,
                    opacity: 1
                  }
                }}
                transition={{
                  duration: 0.5
                }}
              >
                <div className="relative w-full h-[250px] overflow-hidden flex flex-col items-center justify-center">
                  <img key={idx} className="w-full h-full" src={src} alt="project" />
                  <div className="group absolute top-0 left-0 w-full h-full hover:bg-black/50 flex flex-col items-center justify-center transition-all duration-500">
                    <MdOutlineOpenInNew
                      className="text-3xl text-white opacity-0 group-hover:opacity-100 transition-all duration-350 rounded-md cursor-pointer"
                      onClick={() => onImgClick(src)}
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : status === ProjectState.LOADING ? (
          <div className="w-full bg-themeBlue-900 relative flex items-center justify-start gap-[8px] rounded-lg p-5">
            <div className="w-[25px] h-[25px] flex items-center justify-center">
              <Spinner size="sm" color="white" />
            </div>
            <p className="w-[calc(100%-33px)]text-lg text-white font-inter font-light">
              {t('projects.status.loading')}
            </p>
          </div>
        ) : (
          <div className="w-full bg-themePurple-900 relative flex items-center justify-start gap-[8px] rounded-lg p-5">
            <div className="w-[25px] h-[25px] flex items-center justify-center">
              <MdOutlineError className="text-xl text-white" />
            </div>
            <p className="w-[calc(100%-33px)]text-lg text-white font-inter font-light">
              {t('projects.status.empty')}
            </p>
          </div>
        )}
      </div>
      <div className={modal ? 'modal open' : 'modal'}>
        <div
          className="w-[40px] h-[40px] fixed top-5 right-5 bg-themePurple-900/80 hover:bg-themePurple-900 rounded-full flex items-center justify-center transition-all duration-500 cursor-pointer"
          onClick={() => setModal(false)}
        >
          <MdClose size={20} className="text-white" />
        </div>
        <img className="w-full" src={tempImgSrc} alt="pic" />
      </div>
    </Section>
  );
};

export default Projects;
